<template>
  <div class="adyen-tnc">
    <div class="processing-rate">
      <div class="sub-header">
        {{ $t('termsAndCharges.processingCharges') }}
      </div>
      <div class="charges">
        <s-table>
          <s-thead>
            <s-th
              v-for="(tableHeader, index) in tableHeaders" :key="tableHeader" 
              :class="`s-font-bold s-text-neutral-700 s-w-6/12 ${index === 0 ? 's-text-left' : 's-text-right'}`"
            >
              <span class="s-text-[13px]">{{ tableHeader }}</span>
            </s-th>
          </s-thead>
          <s-tbody>
            <s-tr>
              <td style="vertical-align: top" class="s-text-lg">
                {{ $t('termsAndCharges.cardTransaction') }}
              </td>
              <td class="s-text-right">
                <span class="s-font-bold s-text-neutral-700"> {{ cardTransactionDisplayValue }}</span>
              </td>
            </s-tr>
            <s-tr v-if="hasBlendedCardPricing">
              <td style="vertical-align: top" class="s-text-lg">
                {{ $t('termsAndCharges.blendedCardTransaction') }}
              </td>
              <td class="s-text-right">
                <span
                  v-for="(blendedFee, index) in blendedCardTransactionFees"
                  :key="`blended-card-fee-${index}`"
                  class="s-font-bold s-text-neutral-700 s-block"
                >
                  {{ blendedFee }}
                </span>
              </td>
            </s-tr>
            <s-tr v-if="hasBankTransferTransactionFee('ACH_DEBIT')">
              <td style="vertical-align: top" class="s-text-lg">
                {{ $t('termsAndCharges.achTransfer') }}
              </td>
              <td class="s-text-right">
                <span class="s-font-bold s-text-neutral-700">
                  {{ getBankTransferCharge('ACH_DEBIT') }}
                </span>
                <span class="">
                  {{ getBankTransferMinMax('ACH_DEBIT') }}
                </span>
              </td>
            </s-tr>
            <s-tr v-if="hasBankTransferTransactionFee('SEPA_DEBIT')">
              <td style="vertical-align: top" class="s-text-lg">
                {{ $t('termsAndCharges.sepaTransfer') }}
              </td>
              <td class="s-text-right">
                <span class="s-font-bold s-text-neutral-700">
                  {{ getBankTransferCharge('SEPA_DEBIT') }}
                </span>
                <span class="">
                  {{ getBankTransferMinMax('SEPA_DEBIT') }}
                </span>
              </td>
            </s-tr>
            <s-tr v-if="hasBankTransferTransactionFee('BACS_DEBIT')">
              <td style="vertical-align: top" class="s-text-lg">
                {{ $t('termsAndCharges.bacsTransfer') }}
              </td>
              <td class="s-text-right">
                <span class="s-font-bold s-text-neutral-700">
                  {{ getBankTransferCharge('BACS_DEBIT') }}
                </span>
                <span class="">
                  {{ getBankTransferMinMax('BACS_DEBIT') }}
                </span>
              </td>
            </s-tr>
          </s-tbody>
        </s-table>
      </div>
      <i18n path="termsAndCharges.processingRateInstruction" tag="div">
        <template #feeSchedule>
          <c-link :href="feeScheduleUrl" target="_blank">
            {{ $t('termsAndCharges.feeSchedule') }}
          </c-link>
        </template>
      </i18n>
    </div>
    <div class="terms-and-conditions">
      <div class="sub-header-container">
        <div class="sub-header">
          {{ $t('termsAndCharges.termsAndConditions') }}
        </div>
      </div>
      <div class="terms-to-accept">
        <label class="container">
          {{ $t('termsAndCharges.acceptPrompt') }}
          <ul>
            <i18n path="termsAndCharges.guidelinesConditionPayfac" tag="li">
              <template #chargebeeTerms>
                <c-link href="https://www.chargebee.com/embedded-payments-tou/" target="_blank">
                  {{ $t('termsAndCharges.chargebeeTerms') }}
                </c-link>
              </template>
              <template #payfacTerms>
                <div style="display: inline-block;">
                  {{ $t('termsAndCharges.joinTermsOfService') }}
                </div>
                <c-link href="https://www.adyen.com/legal/adyen-for-platforms-terms-and-conditions" target="_blank">
                  {{ $t('termsAndCharges.adyenTerms') }}
                </c-link>
              </template>
            </i18n>
            <li>
              {{ $t('termsAndCharges.guidelinesCondition') }}
            </li>
            <i18n path="termsAndCharges.saqaCondition" tag="li">
              <template #pciDssRequirements>
                <c-link href="https://www.chargebee.com/anv-pci-dss-saq/" target="_blank">
                  {{ $t('termsAndCharges.pciDssRequirements') }}
                </c-link>
              </template>
            </i18n>
            <li>
              {{ $t('termsAndCharges.chargebeeArchivalConditions') }}
              <ul>
                <li>
                  {{ $t('termsAndCharges.chargebeeArchivalSteps') }}
                </li>
              </ul>
            </li>
          </ul>
        </label>
      </div>
      
      <!-- New consent checkbox for recurring charges -->
      <div class="terms-to-accept">
        <label class="container consent-checkbox">
          {{ $t('termsAndCharges.recurringChargesConsent') }}
          <input type="checkbox" @click="(event) => toggleRecurringChargesConsent(event.target.checked)" />
          <span class="checkmark"></span>
        </label>
      </div>

      <i18n path="termsAndCharges.assistance" tag="div" class="title-description s-text-sm">
        <template #faqPage>
          <c-link href="https://www.chargebee.com/docs/2.0/cb_payments_faq.html" target="_blank">
            {{ $t('termsAndCharges.faqPage') }}
          </c-link>
        </template>
        <template #documentationPage>
          <c-link href="https://www.chargebee.com/docs/2.0/cb_payments.html" target="_blank">
            {{ $t('termsAndCharges.documentationPage') }}
          </c-link>
        </template>
      </i18n>
    </div>
  </div>
</template>
<script>


import { BankTransferType, Country } from "@/app/utils/common/constants";
import {
  formatCardTransactionFee,
  getBlendedCardPricingDetails,
  hasBlendedCardPricing,
} from "@/app/utils/common/functions/cardPricingFormatter";
import { mapActions, mapGetters } from "vuex";

export default {
  name: "AdyenTermsAndConditionsV2",
  props: {
    closePopup: {
      type: Function,
      required: true,
    },
  },
  data() {
    return {
      recurringChargesConsentAccepted: false
    };
  },
  computed: {
    tableHeaders() {
      return [
        this.$t('termsAndCharges.component'),
        this.$t('termsAndCharges.chargesPerTransaction'),
      ];
    },
    ...mapGetters('app', ['getCardPricing', 'getACHPricing', 'getBACSPricing', 'getSEPAPricing']),
    ...mapGetters('bankAccountForPayouts', ['getBankTransferType']),
    ...mapGetters('businessAddress', ['getCountry']),
    cardTransactionFee() {
      return formatCardTransactionFee(
        this.getCardPricing.basis_points,
        this.getCardPricing.fixed_rate,
        this.getCurrency(),
      );
    },
    cardPricingTypeLabel() {
      const pricingType = this.getCardPricing?.pricing_type;
      if (!pricingType) {
        return '';
      }

      if (pricingType === 'IC_PLUS_PLUS') {
        return this.$t('termsAndCharges.icPlusPlus');
      }

      if (pricingType === 'BLENDED_PRICING_FLAT_RATE') {
        return this.$t('termsAndCharges.blendedPricingFlatRate');
      }

      return pricingType;
    },
    cardTransactionDisplayValue() {
      if (!this.cardPricingTypeLabel) {
        return this.cardTransactionFee;
      }
      return `${this.cardPricingTypeLabel}: ${this.cardTransactionFee}`;
    },
    blendedCardTransactionFees() {
      return getBlendedCardPricingDetails(this.getCardPricing, this.getCurrency());
    },
    hasBlendedCardPricing() {
      return hasBlendedCardPricing(this.getCardPricing);
    },
    feeScheduleUrl() {
      const merchantCountry = this.getCountry;

      switch (merchantCountry) {
        case Country.GBR:
          return 'https://www.chargebee.com/gb/embedded-payments-fs';
        case Country.FRA:
          return 'https://www.chargebee.com/fr/embedded-payments-fs';
        case Country.DEU:
          return 'https://www.chargebee.com/de/embedded-payments-fs';
        case Country.ESP:
          return 'https://www.chargebee.com/es/embedded-payments-fs';
        case Country.NLD:
          return 'https://www.chargebee.com/nl/embedded-payments-fs';
        case Country.BEL:
          return 'https://www.chargebee.com/be/embedded-payments-fs';
        case Country.ITA:
          return 'https://www.chargebee.com/it/embedded-payments-fs';
        default:
          return 'https://www.chargebee.com/embedded-payments-fs/';
      }
    },
  },
  methods: {
    ...mapActions('agreementAcceptance', ['acceptAgreement']),
    async accept() {
      await this.acceptAgreement(true);
    },
    toggleRecurringChargesConsent(checked) {
      this.recurringChargesConsentAccepted = checked;
    },
    hasBankTransferTransactionFee(type) {
      let pricing;

      if (type === BankTransferType.ACH_DEBIT) {
        pricing = this.getACHPricing;
      } else if (type === BankTransferType.SEPA_DEBIT) {
        pricing = this.getSEPAPricing;
      } else if (type === BankTransferType.BACS_DEBIT) {
        pricing = this.getBACSPricing;
      }

      if (!pricing) {
        return false;
      }

      // Check if any of the relevant fields exist and are greater than 0
      return (
          (pricing.basis_points && pricing.basis_points > 0) ||
          (pricing.fixed_rate && pricing.fixed_rate > 0) ||
          (pricing.min_fee && pricing.min_fee > 0) ||
          (pricing.max_fee && pricing.max_fee > 0)
      );
    },
    getCurrency() {
      if (this.getBankTransferType === BankTransferType.ACH_DEBIT) {
        return '$';
      } else if (this.getBankTransferType === BankTransferType.SEPA_DEBIT) {
        return '€';
      } else {
        return '£';
      }
    },
    // Retrieve the correct pricing based on the transfer type
    getPricingByType(type) {
      if (type === BankTransferType.ACH_DEBIT) {
        return this.getACHPricing;
      } else if (type === BankTransferType.SEPA_DEBIT) {
        return this.getSEPAPricing;
      } else if (type === BankTransferType.BACS_DEBIT) {
        return this.getBACSPricing;
      }
    },

    // Calculate the bank transfer fee based on pricing
    bankTransferTransactionFee(type) {
      const pricing = this.getPricingByType(type);
      return this.bankTransferFee(pricing);
    },
    getBankTransferCharge(type) {
      const bankTransferPricing = this.getPricingByType(type);
      if (!bankTransferPricing) {
        throw new Error("Invalid configuration. Please contact support.");
      }

      const components = [];

      // Add basis points if greater than 0
      if (bankTransferPricing.basis_points > 0) {
        components.push((bankTransferPricing.basis_points / 100) + "%");
      }

      // Add fixed rate if greater than 0
      if (bankTransferPricing.fixed_rate > 0) {
        if (components.length > 0) {
          components.push("+ ");
        }
        components.push(this.getCurrency() + (bankTransferPricing.fixed_rate / 100).toFixed(2));
      }

      return components.join(" ");
    },

    getBankTransferMinMax(type) {
      const bankTransferPricing = this.getPricingByType(type);
      if (!bankTransferPricing) {
        throw new Error("Invalid configuration. Please contact support.");
      }

      let feeRange = "";

      if (bankTransferPricing.min_fee > 0) {
        feeRange = this.getCurrency() + (bankTransferPricing.min_fee / 100).toFixed(2) + " minimum";
      }

      if (bankTransferPricing.max_fee > 0) {
        feeRange += (feeRange ? ", " : "") + this.getCurrency() + (bankTransferPricing.max_fee / 100).toFixed(2) + " maximum";
      }

      return feeRange ? `(${feeRange})` : "";
    },

    // Compute the bank transfer fee structure
    bankTransferFee(bankTransferPricing) {
      if (!bankTransferPricing) {
        throw new Error("Invalid configuration. Please contact support.");
      }

      const components = [];

      // Add basis points if greater than 0
      if (bankTransferPricing.basis_points > 0) {
        components.push((bankTransferPricing.basis_points / 100) + "%");
      }

      // Add fixed rate if greater than 0
      if (bankTransferPricing.fixed_rate > 0) {
        if (components.length > 0) {
          components.push("+ ");
        }
        components.push(this.getCurrency() + (bankTransferPricing.fixed_rate / 100).toFixed(2));
      }

      // Prepare min/max fees if either is greater than 0
      let feeRange = "";
      if (bankTransferPricing.min_fee > 0) {
        feeRange = this.getCurrency() + (bankTransferPricing.min_fee / 100).toFixed(2) + " minimum";
      }
      if (bankTransferPricing.max_fee > 0) {
        feeRange += (feeRange ? ", " : "") + this.getCurrency() + (bankTransferPricing.max_fee / 100).toFixed(2) + " maximum";
      }

      // Add the fee range if min or max fees are present
      if (feeRange) {
        components.push(feeRange);
      }

      // If both components exist, do not use " + " between them
      return components.join(" ");
    },
  },
}
</script>

<style lang="scss">


.adyen-tnc {

  & .buttons {
    display: flex;
    position: sticky;
    bottom: 0;
    justify-content: end;
    gap: 20px;
    font-weight: $weight_bold;
    background-color: $color-white;

    & button {
      margin-top: 10px;
      margin-bottom: 16px;
    }
  }

  & .processing-rate {
    text-align: left;
    margin-top: 16px;
    margin-bottom: 26px;

    & .sub-header {
      margin-bottom: 8px;
      color: $text-color;
    }

    & .charges {
      margin-top: 20px;

      & .charge-item {
        margin-bottom: 8px;
        margin-top: 12px;

      }
    }
  }

  & .sub-header {
    color: $text-color;
    font-size: 16px;
    font-weight: $weight_bolder;
  }

  & .sub-header-container {
    margin-bottom: 20px;
  }

  & .terms-and-conditions {
    text-align: left;
    margin-top: 10px;

    & .conditions {

      & li {
        line-height: 1.5;
        margin: 10px;
      }
    }

    & .charges {

      & .charge-item {
        margin-bottom: 8px;
        margin-top: 8px;
      }
    }
  }

  & .terms-to-accept {

    .container {
      display: block;
      position: relative;
      margin-bottom: 12px;
      cursor: pointer;
      user-select: none;
      line-height: 1.4;

      & ul {
        padding-left: 20px;
      }
    }

    /* Hide the browser's default checkbox */
    .container input {
      position: absolute;
      opacity: 0;
      cursor: pointer;
      height: 0;
      width: 0;
    }

    /* Create a custom checkbox */
    .checkmark {
      position: absolute;
      top: 0;
      left: 0;
      height: 20px;
      width: 20px;
      border: 1px solid $disablea_text;
      border-radius: 4px;
    }

    /* When the checkbox is checked, add a blue background */
    .container input:checked ~ .checkmark {
      background-color: $primary_fill;
    }

    /* Create the checkmark/indicator (hidden when not checked) */
    .checkmark:after {
      content: "";
      position: absolute;
      display: none;
    }

    /* Show the checkmark when checked */
    .container input:checked ~ .checkmark:after {
      display: block;
    }

    /* Style the checkmark/indicator */
    .container .checkmark:after {
      border: solid white;
      border-width: 0 3px 3px 0;
      transform: rotate(45deg);
      left: 6px;
      top: 3px;
      width: 6px;
      height: 11px;
    }
  }

  /* Specific styling for consent checkbox to prevent overlap */
  & .consent-checkbox {
    margin-top: 20px;
    padding-left: 35px;
    
    /* Ensure proper spacing for the checkbox text */
    & .checkmark {
      position: absolute;
      top: 2px;
      left: 0;
    }
  }

}


</style>
